const GALLERY_SELECTOR = '[data-sarky-card-gallery]';
const READY_ATTRIBUTE = 'data-sarky-gallery-ready';
const MIN_DISTANCE = 34;
const AXIS_RATIO = 1.25;
const DESKTOP_MEDIA = '(min-width: 750px)';
const SELECT_EVENT = 'slideshow:select';

function suppressNextClick(gallery) {
  gallery.dataset.sarkySuppressClick = 'true';

  window.setTimeout(() => {
    delete gallery.dataset.sarkySuppressClick;
  }, 450);
}

function isVisibleSlide(slide) {
  return slide instanceof HTMLElement && (!slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
}

function getSlides(slideshow) {
  const refSlides = slideshow.refs?.slides;

  if (Array.isArray(refSlides) && refSlides.length > 0) {
    return refSlides.filter(isVisibleSlide);
  }

  return Array.from(slideshow.querySelectorAll('slideshow-slide')).filter(isVisibleSlide);
}

function wrapIndex(index, length) {
  if (length <= 0) return 0;

  return ((index % length) + length) % length;
}

function getCurrentIndex(slideshow, slides) {
  const storedIndex = Number(slideshow.dataset.sarkyCurrentIndex);

  if (Number.isInteger(storedIndex) && slides[storedIndex]) {
    return storedIndex;
  }

  const currentIndex = Number(slideshow.current);

  if (Number.isInteger(currentIndex) && slides[currentIndex]) {
    return currentIndex;
  }

  const activeSlide = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
  const activeIndex = slides.indexOf(activeSlide);

  return activeIndex >= 0 ? activeIndex : 0;
}

function scrollToSlide(scroller, slide) {
  if (typeof scroller.scrollTo === 'function') {
    scroller.scrollTo({
      left: slide.offsetLeft,
      top: 0,
      behavior: 'auto',
    });

    return;
  }

  scroller.scrollLeft = slide.offsetLeft;
}

function commitSlideSelection(slideshow, scroller, slides, index) {
  const slide = slides[index];

  if (!slide) return;

  slides.forEach((item, itemIndex) => {
    item.setAttribute('aria-hidden', itemIndex === index ? 'false' : 'true');
  });

  scrollToSlide(scroller, slide);

  try {
    slideshow.current = index;
  } catch (_error) {
    slideshow.dataset.sarkyCurrentIndex = String(index);
  }

  slideshow.dataset.sarkyCurrentIndex = String(index);
}

function selectSlide(slideshow, scroller, index, event) {
  const slides = getSlides(slideshow);

  if (slides.length < 2) return;

  const previousIndex = getCurrentIndex(slideshow, slides);
  const nextIndex = wrapIndex(index, slides.length);
  const slide = slides[nextIndex];

  if (!slide) return;

  event?.preventDefault();

  slideshow.setAttribute('actioned', '');

  commitSlideSelection(slideshow, scroller, slides, nextIndex);

  window.requestAnimationFrame(() => {
    commitSlideSelection(slideshow, scroller, slides, nextIndex);
  });

  window.setTimeout(() => {
    commitSlideSelection(slideshow, scroller, slides, nextIndex);
  }, 50);

  slideshow.dispatchEvent(
    new CustomEvent(SELECT_EVENT, {
      bubbles: true,
      detail: {
        index: nextIndex,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      },
    })
  );
}

function keepDesktopArrowsAvailable(gallery) {
  if (gallery.dataset.sarkyDesktopArrows !== 'true') return;

  gallery
    .querySelectorAll('.slideshow-control--previous, .slideshow-control--next')
    .forEach((arrow) => {
      if (!(arrow instanceof HTMLButtonElement)) return;

      arrow.removeAttribute('on:click');
      arrow.type = 'button';
      arrow.disabled = false;
      arrow.removeAttribute('aria-disabled');
    });
}

function initializeGallery(gallery) {
  if (gallery.hasAttribute(READY_ATTRIBUTE)) return;

  const slideshow = gallery.querySelector('slideshow-component');
  const scroller = gallery.querySelector('slideshow-slides');

  if (!slideshow || !scroller) return;

  gallery.setAttribute(READY_ATTRIBUTE, '');
  slideshow.setAttribute('infinite', '');

  keepDesktopArrowsAvailable(gallery);

  slideshow.addEventListener(SELECT_EVENT, (event) => {
    const index = Number(event.detail?.index);
    const slides = getSlides(slideshow);

    if (Number.isInteger(index) && slides[index]) {
      slideshow.dataset.sarkyCurrentIndex = String(index);
    }
  });

  const arrowObserver = new MutationObserver(() => {
    keepDesktopArrowsAvailable(gallery);
  });

  arrowObserver.observe(slideshow, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['disabled', 'aria-disabled', 'on:click'],
  });

  let startX = 0;
  let startY = 0;
  let tracking = false;

  gallery.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const arrow = target.closest('.slideshow-control--previous, .slideshow-control--next');

      const desktopArrowsEnabled =
        gallery.dataset.sarkyDesktopArrows === 'true' && window.matchMedia(DESKTOP_MEDIA).matches;

      if (arrow && gallery.contains(arrow) && desktopArrowsEnabled) {
        event.preventDefault();
        event.stopImmediatePropagation();

        suppressNextClick(gallery);
        keepDesktopArrowsAvailable(gallery);

        const slides = getSlides(slideshow);

        if (slides.length < 2) return;

        const current = getCurrentIndex(slideshow, slides);

        const direction = arrow.classList.contains('slideshow-control--next') ? 1 : -1;

        selectSlide(slideshow, scroller, current + direction, event);
        keepDesktopArrowsAvailable(gallery);

        return;
      }

      if (gallery.dataset.sarkySuppressClick !== 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      delete gallery.dataset.sarkySuppressClick;
    },
    true
  );

  if (gallery.dataset.sarkyDiscreteSwipe !== 'true') return;

  scroller.addEventListener(
    'touchstart',
    (event) => {
      if (window.matchMedia(DESKTOP_MEDIA).matches) return;
      if (event.touches.length !== 1) return;

      const touch = event.touches[0];

      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    },
    { passive: true }
  );

  scroller.addEventListener(
    'touchmove',
    (event) => {
      if (!tracking || event.touches.length !== 1) return;

      const touch = event.touches[0];

      const distanceX = touch.clientX - startX;
      const distanceY = touch.clientY - startY;

      const isHorizontal =
        Math.abs(distanceX) > 10 && Math.abs(distanceX) > Math.abs(distanceY) * AXIS_RATIO;

      if (isHorizontal) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  scroller.addEventListener(
    'touchend',
    (event) => {
      if (!tracking) return;

      tracking = false;

      const touch = event.changedTouches[0];

      if (!touch) return;

      const distanceX = touch.clientX - startX;
      const distanceY = touch.clientY - startY;

      const isHorizontal =
        Math.abs(distanceX) >= MIN_DISTANCE && Math.abs(distanceX) > Math.abs(distanceY) * AXIS_RATIO;

      if (!isHorizontal) return;

      const slides = getSlides(slideshow);

      if (slides.length < 2) return;

      const current = getCurrentIndex(slideshow, slides);
      const direction = distanceX < 0 ? 1 : -1;

      suppressNextClick(gallery);
      selectSlide(slideshow, scroller, current + direction, event);
    },
    { passive: true }
  );

  scroller.addEventListener(
    'touchcancel',
    () => {
      tracking = false;
    },
    { passive: true }
  );
}

function initializeGalleries(root = document) {
  root.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
    if (gallery instanceof HTMLElement) {
      initializeGallery(gallery);
    }
  });
}

initializeGalleries();

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.matches(GALLERY_SELECTOR)) {
        initializeGallery(node);
      }

      initializeGalleries(node);
    }
  }
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
});